import ModalSearchLayout from "@/components/layout/ModalSearchLayout";
import { Button } from "@/components/ui/Button";
import { InputBox } from "@/components/ui/InputBox";
import Table, { TableColumn } from "@/components/ui/Table";
import { useInfiniteScrollPage } from "@/hooks/components/useInfiniteScrollPage";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useCompanyCode } from "@/hooks/standard/st_company_code/useCompanyCode";
import { StCompanyCode } from "@/interface/standard/st_company_code";
import CountBox from "@/components/ui/CountBox";

interface CompanySearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (item: StCompanyCode) => void;
}

export default function CompanySearchModal({ isOpen, onClose, onSelect }: CompanySearchModalProps) {
  const [searchKeyword, setSearchKeyword] = useState("");
  const [actualSearchValue, setActualSearchValue] = useState("");
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [allData, setAllData] = useState<StCompanyCode[]>([]);
  const [tableVer, setTableVer] = useState(0);

  // 페이지네이션을 지원하는 useCompanyCode 호출
  const { data: companyCodeData, isLoading } = useCompanyCode({
    page: currentPage,
    size: 20,
    sort: "stCompanyCode,desc",
    search: actualSearchValue || undefined,
  });

  // 검색어가 변경될 때 페이지 초기화 (allData는 비우지 않음 - 새 데이터가 오면 교체됨)
  useEffect(() => {
    setCurrentPage(0);
    setTotalElements(0);
    setLoadingMore(false);
  }, [actualSearchValue]);

  // 새로운 페이지 데이터가 로드되면 누적 데이터에 추가
  useEffect(() => {
    if (companyCodeData && companyCodeData.content && companyCodeData.content.length > 0) {
      // 첫 번째 페이지(currentPage === 0)이거나 검색이 변경된 경우 기존 데이터 교체
      if (currentPage === 0) {
        setAllData(companyCodeData.content);
        setTotalElements(companyCodeData.totalElements || 0);
      } else {
        // 추가 페이지는 기존 데이터에 추가
        setAllData(prev => [...prev, ...companyCodeData.content]);
      }
    } else if (companyCodeData && (!companyCodeData.content || companyCodeData.content.length === 0)) {
      // 데이터가 없는 경우 빈 배열로 설정
      setAllData([]);
      setTotalElements(0);
    }
  }, [companyCodeData, currentPage]);

  // 로딩 상태 업데이트
  useEffect(() => {
    if (companyCodeData) {
      setLoadingMore(false);
    }
  }, [companyCodeData]);

  // hasMore 상태를 useMemo로 계산
  const hasMore = useMemo(() => {
    if (!companyCodeData) return false;
    return !companyCodeData.last && allData.length < totalElements;
  }, [companyCodeData?.last, allData.length, totalElements]);

  const infiniteScrollPage = useInfiniteScrollPage({
    data: allData,
    initialLoadCount: 20,
    loadMoreCount: 20,
    hasMore: hasMore,
    onDataLoad: async currentCount => {
      if (hasMore && !loadingMore) {
        setLoadingMore(true);
        setCurrentPage(prev => prev + 1);
      }
    },
  });

  const handleSearch = useCallback(() => {
    setActualSearchValue(searchKeyword);
    setTableVer(v => v + 1);
  }, [searchKeyword]);

  useEffect(() => {
    setTableVer(v => v + 1);
  }, [actualSearchValue]);

  const handleRowDoubleClick = useCallback(
    (item: StCompanyCode) => {
      onSelect(item);
      onClose();
    },
    [onSelect, onClose]
  );

  const handleConfirm = useCallback(() => {
    if (selectedRows.length === 0) {
      return;
    }
    if (selectedRows.length === 1) {
      const selectedItem = allData[selectedRows[0]];
      onSelect(selectedItem);
      onClose();
    }
  }, [selectedRows, allData, onSelect, onClose]);

  // 테이블 컬럼 정의 (이미지 기준)
  const columns: TableColumn[] = [
    { key: "stCompanyCode", header: "코드", render: v => String(v).padStart(5, "0") },
    { key: "stCompanyName", header: "업체명", bodyAlign: "left" },
    { key: "stCompanyNameshort", header: "줄임상호", bodyAlign: "left" },
    { key: "stCompanyNumber", header: "사업자번호" },
    { key: "stCompanyOwnername", header: "대표자", bodyAlign: "left" },
    { key: "stCompanyTel", header: "전화번호" },
    { key: "stCompanyFax", header: "팩스번호" },
    { key: "stCompanyMovetel", header: "Mobile" },
    { key: "stCompanyRemark", header: "비고", bodyAlign: "left" },
  ];

  // 모달 열릴 때 초기 데이터 로드
  useEffect(() => {
    if (isOpen) {
      // 모달이 열릴 때 상태 초기화 (allData는 유지하여 캐시 활용)
      setSearchKeyword("");
      setSelectedRows([]);
      setCurrentPage(0);
      setLoadingMore(false);
      setActualSearchValue("");
      setTableVer(v => v + 1);
      // 캐시된 데이터가 있으면 바로 표시
      if (companyCodeData?.content) {
        setAllData(companyCodeData.content);
        setTotalElements(companyCodeData.totalElements || 0);
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <ModalSearchLayout isOpen={isOpen} onClose={onClose} title="거래처 검색" width="w-[1400px]">
      <div className="h-full flex flex-col">
        {/* 검색 조건 */}
        <div className="mb-3 flex-shrink-0">
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <InputBox
                label="검색명"
                value={searchKeyword}
                onChange={e => setSearchKeyword(e.target.value)}
                placeholder="검색어를 입력하세요"
                onKeyDown={e => {
                  if (e.key === "Enter") {
                    handleSearch();
                  }
                }}
              />
            </div>
            <div className="flex-shrink-0">
              <Button variant="primary" onClick={handleSearch}>
                조회
              </Button>
            </div>
          </div>
        </div>

        {/* 테이블 */}
        <div className="flex-1 min-h-0">
          <Table
            showRowNumber
            highlightOnClick={true}
            key={`company-search-table-v${tableVer}`}
            {...infiniteScrollPage.tableProps}
            columns={columns}
            data={allData}
            onRowDoubleClick={handleRowDoubleClick}
            onRowSelect={setSelectedRows}
            maxHeight="calc(100vh - 350px)"
            loadingMore={loadingMore}
            loading={isLoading && currentPage === 0}
            emptyMessage="검색 결과가 없습니다."
          />
        </div>

        {/* 하단 컨트롤 */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-300 flex-shrink-0">
          <div className="flex items-center gap-2">
            <CountBox label="조회" count={totalElements} />
          </div>
          <div className="flex gap-2">
            <Button variant="primary" onClick={handleConfirm} disabled={selectedRows.length !== 1}>
              확인
            </Button>
            <Button variant="white" onClick={onClose}>
              닫기
            </Button>
          </div>
        </div>
      </div>
    </ModalSearchLayout>
  );
}
